/**********************************************************************
 *
 * Copyright (C) 2026 Almendo Technologies GmbH
 * Copyright (C) 2026 Marcus Harringer
 *
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of
 * Marcus Harringer. The intellectual and technical concepts contained
 * herein are proprietary to Marcus Harringer and may be covered
 * copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Marcus Harringer.
 *
 ***********************************************************************/
import { ssoConfig } from '../config';

// Return pages of the hosted UI, registered as callback and logout URLs of the Cognito app client
export const SSO_CALLBACK_LOGIN = '/callback/login';
export const SSO_CALLBACK_LOGOUT = '/callback/logout';

// Cognito identity provider of personal Microsoft accounts, same name in every environment
export const SSO_PROVIDER_MICROSOFT = 'microsoft';

export const isSsoEnabled = (config = ssoConfig) => Boolean(config.loginDomain);

export const getMailDomain = (email) => {
  const at = (email ?? '').lastIndexOf('@');
  return at < 0 ? null : email.slice(at + 1).trim().toLowerCase() || null;
};

// Home realm discovery on the public API (public.<domain>, no sign-in needed)
export const SSO_DISCOVERY_PATH = '/v1/sso/discovery';

// Home realm discovery: does the mail domain sign in with a company account? Asked before the redirect, an
// unknown domain would end on Cognito's own login page. Without public API or when it fails, the redirect decides.
export const hasCompanyAccount = async (email, config = ssoConfig) => {
  const domain = getMailDomain(email);
  if (!config.publicApiUrl || !domain) {
    return true;
  }
  try {
    const response = await fetch(`${config.publicApiUrl}${SSO_DISCOVERY_PATH}?domain=${encodeURIComponent(domain)}`);
    if (!response.ok) {
      return true;
    }
    return (await response.json()).sso === true;
  } catch (err) {
    console.error(err);
    return true;
  }
};

// The redirect to the identity provider leaves the app: remember the page the login was shown on
// (AuthGuard shows it on the requested page) and return there after the sign-in.
const RETURN_PATH_KEY = 'blusensor-sso-return-path';
const RETURN_PATH_DEFAULT = '/';

export const storeSsoReturnPath = (location = window.location) => {
  const path = `${location.pathname}${location.search}`;
  const isAuthPage = location.pathname.startsWith('/authentication') || location.pathname.startsWith('/callback');
  try {
    window.sessionStorage.setItem(RETURN_PATH_KEY, isAuthPage ? RETURN_PATH_DEFAULT : path);
  } catch (err) {
    // without session storage the sign-in ends on the default page
  }
};

export const takeSsoReturnPath = () => {
  try {
    const path = window.sessionStorage.getItem(RETURN_PATH_KEY);
    window.sessionStorage.removeItem(RETURN_PATH_KEY);
    return path?.startsWith('/') && !path.startsWith('//') ? path : RETURN_PATH_DEFAULT;
  } catch (err) {
    return RETURN_PATH_DEFAULT;
  }
};

// Cognito passes the rejection of the pre sign-up trigger as error_description, Amplify hands it on URL encoded
export const decodeSsoLoginError = (message) => {
  const text = String(message ?? '').replace(/\+/g, ' ');
  try {
    return decodeURIComponent(text);
  } catch (err) {
    return text;
  }
};

const SSO_LOGIN_ERRORS = [
  { match: 'mail domain does not belong to this organization', key: 'NLS_SSO_LOGIN_ERROR_DOMAIN' },
  { match: 'please sign in with your company account', key: 'NLS_SSO_LOGIN_ERROR_COMPANY' },
  { match: 'please sign in with your password', key: 'NLS_SSO_LOGIN_ERROR_PASSWORD' },
  { match: 'attributes required: [email]', key: 'NLS_SSO_LOGIN_ERROR_EMAIL' },
  { match: 'sent no mail address', key: 'NLS_SSO_LOGIN_ERROR_EMAIL' }
];

// NLS key (namespace sso) explaining a failed sign-in
export const getSsoLoginErrorKey = (message) => {
  const text = decodeSsoLoginError(message).toLowerCase();
  return SSO_LOGIN_ERRORS.find((error) => text.includes(error.match))?.key ?? 'NLS_SSO_LOGIN_ERROR_TEXT';
};

// Accounts at identity providers the user signs in with (ID token claim "identities"). A provider account that created
// the user is primary and the user has no password; a provider account linked to a password account is not primary.
export const getSsoIdentities = (idTokenPayload) => {
  let identities = idTokenPayload?.identities ?? [];
  if (typeof identities === 'string') {
    try {
      identities = JSON.parse(identities);
    } catch (err) {
      identities = [];
    }
  }
  if (!Array.isArray(identities)) {
    return [];
  }
  return identities.map((identity) => ({
    provider: identity.providerName,
    company: identity.providerName !== SSO_PROVIDER_MICROSOFT,
    passwordless: String(identity.primary) === 'true',
    since: identity.dateCreated ? new Date(Number(identity.dateCreated)) : null
  }));
};

export const hasPassword = (ssoIdentities = []) => !ssoIdentities.some((identity) => identity.passwordless);
